import styled from 'styled-components'

import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { IconCheck } from 'components/Icons/IconCheck'
import { IconInfo } from 'components/Icons/InfoIcon'
import { Menu } from 'components/Menu/Menu'
import { Typography } from 'components/Typography/Typography'
import { useCallback, useRef, useState } from 'react'
import { useOnClickOutside } from 'utils/onClickOutside'
import { useIsMobile } from 'utils/useIsMobile'
import { Tooltip } from 'components/Tooltip/Tooltip'

const SelectProtocol = styled(Typography)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

const MenuTrigger = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 16px;
  border-radius: 8px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  cursor: pointer;
  div {
    display: flex;
    align-items: center;
  }
`

const StyledMenuItem = styled.div`
  & > div {
    padding: 16px;
    display: flex;
    align-items: center;
    cursor: pointer;
    &:hover {
      background-color: ${p => p.theme.colors.background.hover};
    }
  }
`

interface MenuItemProps {
  item: ProtocolOption
  activeOption: ProtocolOption | null
  onClick: (o: ProtocolOption) => void
}

const MenuItem = ({ item, onClick, activeOption }: MenuItemProps) => {
  const isMobile = useIsMobile()
  return (
    <StyledMenuItem onClick={() => onClick(item)}>
      <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
        <img src={item.img} style={{ width: 24, height: 24, marginRight: 8 }} />
        {item.name}
        {item.id === activeOption?.id && (
          <div style={{ marginLeft: 'auto', display: 'flex' }}>
            <IconCheck />
          </div>
        )}
      </Typography>
    </StyledMenuItem>
  )
}

export interface ProtocolOption {
  img: string
  name: string
  id: string
}

interface Props {
  activeOption: ProtocolOption | null
  onOptionClick: (o: ProtocolOption) => void
  options: ProtocolOption[]
}

export const SelectProtocolMenu = ({ activeOption, onOptionClick, options }: Props) => {
  const isMobile = useIsMobile()

  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuNode = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutside(menuNode, onClose)

  const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

  const handleOptionClick = useCallback(
    (o: ProtocolOption) => {
      onOptionClick(o)
      toggleMenu()
    },
    [onOptionClick, toggleMenu]
  )

  return (
    <div style={{ position: 'relative' }}>
      <SelectProtocol variant="label2" color="secondary" fontWeight="medium">
        Select Protocol
        <Tooltip
          content="Choose the decentralized exchange or protocol where your liquidity will be deployed."
          position="top"
        >
          <IconInfo width={16} height={16} style={{ marginLeft: 6 }} />
        </Tooltip>
      </SelectProtocol>
      <MenuTrigger onClick={toggleMenu}>
        <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
          {activeOption ? <img src={activeOption.img} style={{ width: 24, height: 24, marginRight: 8 }} /> : null}
          {activeOption ? activeOption.name : null}
        </Typography>
        <div>
          <IconArrowDown />
        </div>
      </MenuTrigger>
      {isMenuOpen && (
        <Menu anchorElement={menuNode} style={{ top: 112, width: '100%', padding: '8px 0', zIndex: 1 }}>
          {options.map(o => (
            <MenuItem key={o.id} item={o} onClick={handleOptionClick} activeOption={activeOption} />
          ))}
        </Menu>
      )}
    </div>
  )
}
